import {  html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { FormBase } from "./formBase";

interface FormData {
  _details: any;
  title?: string;
  // Add other properties that might exist on af/feed objects
}


@customElement('accordion-x')
export class AccordionX extends FormBase {
  static styles = css`
    :host {
      display: block;
      width: 100%;
      --accordion-transition-duration: 300ms;
      --accordion-easing: cubic-bezier(0.4, 0, 0.2, 1);
    }

    .accordion-x {
      width: 100%;
      margin-bottom: 16px;
    }

    .accordion-header {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 16px;
      background: var(--md-sys-color-surfaceContainerLowest);
      border: 1px solid var(--md-sys-color-outlineVariant);
      cursor: pointer;
      text-align: left;
      transition: all var(--accordion-transition-duration) var(--accordion-easing);
      user-select: none;
      min-height: 64px;
      gap: 12px;
    }

    .accordion-header:hover {
      background: var(--md-sys-color-surfaceContainerLow);
      border-color: var(--md-sys-color-outline);
    }

    .accordion-header:focus-visible {
      outline: 2px solid var(--md-sys-color-primary);
      outline-offset: 2px;
    }

    .accordion-header.expanded {
      border-color: var(--md-sys-color-primary);
      border-width: 2px;
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }

    .accordion-title {
      flex: 1;
      font-size: 16px;
      font-weight: 500;
      line-height: 1.5;
      color: var(--md-sys-color-onSurface);
      margin: 0;
    }

    .accordion-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform var(--accordion-transition-duration) var(--accordion-easing);
    }

    .accordion-icon.expanded {
      transform: rotate(180deg);
    }

    .accordion-content-wrapper {
      overflow: hidden;
      max-height: 0;
      opacity: 0;
      transition: 
        max-height var(--accordion-transition-duration) var(--accordion-easing),
        opacity var(--accordion-transition-duration) var(--accordion-easing);
      background: var(--md-sys-color-surfaceContainerLowest);
      border-top: none;
      border-bottom-left-radius: 12px;
      border-bottom-right-radius: 12px;
    }

    .accordion-content-wrapper.expanded {
      max-height: 2000px; /* Adjust based on content */
      opacity: 1;
    }

    .accordion-content {
      padding: 16px;
    }

    /* Responsive design */
    @media (max-width: 768px) {
      .accordion-header {
        padding: 12px;
        min-height: 56px;
      }
      
      .accordion-title {
        font-size: 14px;
      }
      
      .accordion-content {
        padding: 12px;
        padding-top: 0;
      }
    }

    /* Dark mode support */
    .dark-mode {
      --md-sys-color-surfaceContainerLowest: #1c1c1e;
      --md-sys-color-surfaceContainerLow: #2c2c2e;
      --md-sys-color-outlineVariant: #38383a;
      --md-sys-color-outline: #48484a;
      --md-sys-color-onSurface: #ffffff;
      --md-sys-color-primary: #0a84ff;
    }
  `;

  @property({ type: String }) title = '';
  @property({ type: Object }) oe: any = {};
  @property({ type: Object }) fr: any = {};
  @property({ type: String }) service = '';
  @property({ type: Array }) pageGroups: any[] = [];
  @property({ type: String }) currentPageGroupName = '';
  @property({ type: String }) sectionSize = '';
  @property({ type: Object }) details: any = {};
  @property({ type: String }) activeAccordion: string | null = null;
  @property({ type: Function }) setActiveAccordion?: (id: string | null) => void;
  @property({ type: Function }) skipForm?: () => void;
  @property({ type: Function }) _dialog?: any;
  @property({ type: Function }) getForm2?: (form: HTMLElement | null) => void;
  @property({ type: Object }) af: FormData = {
    _details: undefined
  };
  @property({ type: Object }) feed: FormData = {
    _details: undefined
  };
  @property({ type: String }) rendermode = '';

  @state()
  private expanded = false;

  private accordionId: string = '';

  constructor() {
    super();
    this.accordionId = `accordion-${Math.random().toString(36).substr(2, 9)}`;
  }

  connectedCallback() {
    super.connectedCallback();
    this.updateExpandedState();
  }

willUpdate(changedProperties: Map<string, any>) {
  if (changedProperties.has('title') || changedProperties.has('service')) {
    this.accordionId = this.generateAccordionId();
  }

  if (changedProperties.has('activeAccordion')) {
    this.updateExpandedState();
  }
}

  private generateAccordionId(): string {
    const baseId = `${this.service}-${this.title}`
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    
    return `accordion-${baseId}`;
  }

private updateExpandedState() {
  if (this.setActiveAccordion) {
    // Use centralized control
    this.expanded = this.activeAccordion === this.accordionId;
  } else {
    // Fallback to local state
    // (Keep existing logic if needed)
  }
}

private toggleAccordion(e: Event) {
  e.preventDefault();
  e.stopPropagation();

  if (this.setActiveAccordion) {
    // If this accordion is already active, close it
    if (this.activeAccordion === this.accordionId) {
      this.setActiveAccordion(null);
    } else {
      // Otherwise, make this accordion active
      this.setActiveAccordion(this.accordionId);
    }
  } else {
    // Fallback: toggle local state if no centralized control
    this.expanded = !this.expanded;
  }

  // Dispatch custom event
  this.dispatchEvent(new CustomEvent('accordion-toggle', {
    detail: {
      id: this.accordionId,
      title: this.title,
      expanded: this.activeAccordion === this.accordionId,
      service: this.service
    },
    bubbles: true,
    composed: true
  }));
}

  private renderAccordionContent() {
    if (!this.expanded) return null;

    return html`
      <div class="accordion-content-wrapper expanded">
        <div class="accordion-content">
        <slot name="body"></slot>
          
        </div>
      </div>
    `;
  }

  render() {
    // Use custom SVG icon for arrow
    const arrowIcon = html`
      <svg 
        class="accordion-icon ${this.expanded ? 'expanded' : ''}" 
        width="24" 
        height="24" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
    `;

    return html`
      <div class="accordion-x" id="${this.accordionId}">
        <!-- Accordion Header -->
        <button
          type="button"
          class="accordion-header ${this.expanded ? 'expanded' : ''}"
          @click=${this.toggleAccordion}
          aria-expanded="${this.expanded}"
          aria-controls="${this.accordionId}-content"
        >
          <span class="accordion-title">${this.title}</span>
          ${this.af._details.logo ? html`<img src="${this.af._details.logo}" alt="Logo" style="height:24px; width:auto; margin-right:8px;">` : html `
          ${arrowIcon}`}
        </button>

        <!-- Accordion Content -->
        ${this.renderAccordionContent()}
      </div>
    `;
  }
}

// Type declarations for custom elements
declare global {
  interface HTMLElementTagNameMap {
    'accordion-x': AccordionX;
  }
}